if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }


      connectedCallback() {
        if (this.dataset.yakitaAutoplay !== 'true' || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
        if (this.autoplayController) return;
        const images = this.yakitaImages();
        if (images.length < 2) return;
        this.autoplayController = new AbortController();
        const options = { signal: this.autoplayController.signal };
        this.autoplayStopped = false;
        this.autoplayHovered = false;
        this.autoplayButton = document.createElement('button');
        this.autoplayButton.type = 'button';
        this.autoplayButton.className = 'yakita-gallery-autoplay';
        this.autoplayButton.textContent = this.dataset.autoplayPause;
        this.autoplayButton.setAttribute('aria-pressed', 'false');
        this.elements.viewer.after(this.autoplayButton);
        this.autoplayButton.addEventListener('click', () => {
          this.autoplayStopped = !this.autoplayStopped;
          this.updateAutoplayButton();
        }, options);
        this.addEventListener('pointerenter', (event) => {
          if (event.pointerType === 'mouse') this.autoplayHovered = true;
        }, options);
        this.addEventListener('pointerleave', () => { this.autoplayHovered = false; }, options);
        const stopOnInteraction = (event) => {
          if (event.target.closest('.yakita-gallery-autoplay')) return;
          this.autoplayStopped = true;
          this.updateAutoplayButton();
        };
        this.addEventListener('pointerdown', stopOnInteraction, options);
        this.addEventListener('keydown', stopOnInteraction, options);
        this.autoplayTimer = window.setInterval(() => this.advanceYakitaImage(), 5000);
      }

      disconnectedCallback() {
        clearInterval(this.autoplayTimer);
        this.autoplayController?.abort();
        this.autoplayController = null;
        this.autoplayButton?.remove();
      }

      yakitaImages() {
        if (!this.elements.viewer) return [];
        return Array.from(this.elements.viewer.querySelectorAll('[data-media-id]')).filter(
          (slide) => !slide.hidden && !slide.classList.contains('hidden') && slide.querySelector('.product__modal-opener--image img')
        );
      }

      updateAutoplayButton() {
        this.autoplayButton.textContent = this.autoplayStopped ? this.dataset.autoplayPlay : this.dataset.autoplayPause;
        this.autoplayButton.setAttribute('aria-pressed', String(this.autoplayStopped));
      }

      advanceYakitaImage() {
        const bounds = this.getBoundingClientRect();
        if (this.autoplayStopped || this.autoplayHovered || document.hidden ||
            window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
            (this.contains(document.activeElement) && document.activeElement !== this.autoplayButton) ||
            document.querySelector('product-modal[open]') ||
            bounds.bottom <= 0 || bounds.top >= window.innerHeight) return;
        if (this.mql.matches && !this.dataset.desktopLayout.includes('thumbnail')) return;
        const images = this.yakitaImages();
        if (images.length < 2) return;
        const active = this.elements.viewer.querySelector('[data-media-id].is-active');
        if (active && !images.includes(active)) return;
        const next = images[(images.indexOf(active) + 1) % images.length];
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((slide) => slide.classList.toggle('is-active', slide === next));
        next.parentElement.scrollTo({ left: next.offsetLeft, behavior: 'smooth' });
        const thumbnail = this.elements.thumbnails?.querySelector('[data-target="' + next.dataset.mediaId + '"]');
        this.setActiveThumbnail(thumbnail);
      }

     onSlideChanged(event) {
        const currentElement = event.detail?.currentElement;
        if (!currentElement || !currentElement.dataset?.mediaId) return;
      
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
